// src/services/paymentService.js
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL;

const paymentApi = axios.create({
  baseURL: `${API_URL}/api/v1/payments`,
  headers: { 'Content-Type': 'application/json' },
  timeout: 25000
});

// Attach user token
paymentApi.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('kapta_token');
    if (token) config.headers.Authorization = `Bearer ${token}`;
    return config;
  },
  (error) => Promise.reject(error)
);

// Handle 401 globally
paymentApi.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('kapta_token');
      localStorage.removeItem('kapta_user');
      // Let the calling component decide whether to redirect
      console.warn('[paymentService] Session expired');
    }
    return Promise.reject(error);
  }
);

export const paymentService = {
  /**
   * Initialize a payment for an existing order OR create one on the fly.
   *
   * @param {string|null} orderId       - existing order _id (optional)
   * @param {object|null} orderPayload  - full order payload (optional)
   * @returns {Promise<{
   *   success: boolean,
   *   authorizationUrl: string,
   *   accessCode: string,
   *   reference: string,
   *   orderId: string,
   *   orderNumber: string,
   *   amount: number,
   *   transactionId: string
   * }>}
   */
  async initialize(orderId = null, orderPayload = null) {
    const body = {};
    if (orderId) body.orderId = orderId;
    if (orderPayload) body.orderPayload = orderPayload;

    const res = await paymentApi.post('/initialize', body);
    return res.data;
  },

  /**
   * Verify a transaction by reference.
   *
   * @param {string} reference - Paystack reference (from URL query)
   * @returns {Promise<{
   *   success: boolean,
   *   status: 'success' | 'failed' | 'abandoned',
   *   order: object|null,
   *   transaction: object
   * }>}
   */
  async verify(reference) {
    const res = await paymentApi.get(`/verify/${encodeURIComponent(reference)}`);
    return res.data;
  },

  /**
   * Convenience: build an order payload from the local cart + form.
   * Use this to keep the Checkout.jsx handleCompleteOrder lean.
   *
   * @param {Array} items
   * @param {Object} form
   * @param {{ subtotal: number, memoryIncluded: number, shippingFee: number, total: number }} totals
   */
  buildOrderPayload(items, form, totals) {
    return {
      items: items.map((it) => ({
        productId: it.id || null,
        slug: it.slug || null,
        title: it.title,
        description: it.description || '',
        image: it.image || '',
        isCustom: !!it.isCustom,
        unitPrice: it.price,
        quantity: it.quantity,
        lineTotal: it.lineTotal || it.price * it.quantity,
        customization: it.customization || {},
        audioFile: it.audioFile || null,
        audioUrl: it.audioUrl || '',
        personalizedMessage: it.personalizedMessage || '',
        magnets: it.customization?.magnets || []
      })),
      customer: {
        email: form.email,
        phone: form.phone,
        firstName: form.firstName,
        lastName: form.lastName
      },
      delivery: {
        address: form.address,
        city: form.city,
        state: form.state,
        country: form.country,
        deliveryNote: form.deliveryNote
      },
      subtotal: totals.subtotal,
      memoryIncluded: totals.memoryIncluded,
      shippingFee: totals.shippingFee,
      total: totals.total
    };
  }
};

export default paymentService;